import { formatDistanceToNow } from 'date-fns';
import {
  Shield,
  Activity,
  Database,
  CheckCircle,
  XCircle,
  RefreshCw,
  Lock,
  Users,
  Eye
} from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { StatCard } from '@/components/ui/stat-card';
import { LocationFilterCard } from '@/components/admin/location-filter';
import { AiConfigCard } from '@/components/admin/ai-config-card';
import { UserRolesCard } from '@/components/admin/user-roles-card';
import { MatrixBuilderCard } from '@/components/admin/matrix-builder-card';
import {
  useHealthCheck,
  useDataStats,
  useActivityLogs,
  useSyncAssignments
} from '@/hooks/use-admin';
import type { ActivityLogEntry } from '@/hooks/use-admin';
import { useT } from '@/lib/i18n';
import { useCurrentUser } from '@/hooks/use-current-user';
import { apiFetch } from '@/lib/api';

type VisibilityLevel = 'restricted' | 'team-visible' | 'open';

interface VisibilityOption {
  readonly level: VisibilityLevel;
  readonly label: string;
  readonly description: string;
  readonly Icon: React.ComponentType<{ className?: string }>;
}

const VISIBILITY_OPTIONS: readonly VisibilityOption[] = [
  {
    level: 'restricted',
    label: 'Restricted',
    description: 'Members see only their own data',
    Icon: Lock
  },
  {
    level: 'team-visible',
    label: 'Team Visible',
    description: 'Members see team pages but not individual profiles',
    Icon: Users
  },
  {
    level: 'open',
    label: 'Open',
    description: 'Members see everything except admin',
    Icon: Eye
  }
] as const;

function MemberVisibilityCard() {
  const queryClient = useQueryClient();

  const { data } = useQuery({
    queryKey: ['visibility-policy'],
    queryFn: () => apiFetch<{ level: string }>('/admin/visibility-policy')
  });

  const current = (data?.data?.level ?? 'team-visible') as VisibilityLevel;

  const mutation = useMutation({
    mutationFn: (level: VisibilityLevel) =>
      apiFetch('/admin/visibility-policy', {
        method: 'POST',
        body: JSON.stringify({ level })
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['visibility-policy'] });
      void queryClient.invalidateQueries({ queryKey: ['current-user'] });
    }
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-sm">
          <Users className="h-4 w-4" />
          Member Visibility
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          {VISIBILITY_OPTIONS.map(({ level, label, description, Icon }) => {
            const isSelected = current === level;
            return (
              <button
                key={level}
                onClick={() => mutation.mutate(level)}
                disabled={mutation.isPending}
                className={`flex flex-col gap-1.5 rounded-lg border p-3 text-left transition-colors ${
                  isSelected
                    ? 'border-primary bg-primary/10 text-primary dark:bg-primary/20'
                    : 'border-border text-foreground/60 hover:border-primary/50 hover:bg-secondary'
                }`}
              >
                <div className="flex items-center gap-2">
                  <Icon className={`h-4 w-4 ${isSelected ? 'text-primary' : ''}`} />
                  <span className="text-sm font-medium">{label}</span>
                </div>
                <p className="text-xs text-muted-foreground">{description}</p>
              </button>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

const categoryColors: Record<string, string> = {
  admin: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  notification: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
  cron: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  user: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
  ai: 'bg-pink-100 text-pink-800 dark:bg-pink-900/30 dark:text-pink-400'
};

function StatusIcon({ ok }: { ok: boolean }) {
  return ok ? (
    <CheckCircle className="h-4 w-4 text-green-500 dark:text-green-400" />
  ) : (
    <XCircle className="h-4 w-4 text-red-500 dark:text-red-400" />
  );
}

function formatCheckLabel(key: string): string {
  return key
    .replace(/^has/, '')
    .replace(/([A-Z])/g, ' $1')
    .trim();
}

function ActivityRow({ log }: { log: ActivityLogEntry }) {
  const hasDetails = log.details && Object.keys(log.details).length > 0;

  return (
    <div className="flex items-start justify-between rounded-lg border p-3 text-xs">
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <Badge
            variant="outline"
            className={
              categoryColors[log.category] ??
              'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300'
            }
          >
            {log.category}
          </Badge>
          <span className="font-medium">{log.action}</span>
        </div>
        <p className="mt-1 text-muted-foreground">
          by {log.actor}
          {hasDetails && <span> — {JSON.stringify(log.details)}</span>}
        </p>
      </div>
      <span className="ml-2 shrink-0 text-muted-foreground">
        {formatDistanceToNow(new Date(log.createdAt), { addSuffix: true })}
      </span>
    </div>
  );
}

export function AdminPage() {
  const t = useT();
  const { canAccessAdmin } = useCurrentUser();
  const { data: healthData } = useHealthCheck();
  const { data: statsData } = useDataStats();
  const { data: logsData, isLoading: logsLoading } = useActivityLogs(50);
  const syncAssignments = useSyncAssignments();

  const health = healthData?.data;
  const stats = statsData?.data;
  const logs = logsData?.data ?? [];

  if (!canAccessAdmin) {
    return (
      <div className="py-12 text-center">
        <h2 className="text-lg font-semibold">Access Restricted</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          You don't have permission to view this page. Contact your admin for access.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        <Shield className="h-5 w-5 text-primary" />
        <h1 className="text-2xl font-semibold">{t('admin.title')}</h1>
        <div className="ml-auto">
          <Button
            size="sm"
            onClick={() => syncAssignments.mutate()}
            disabled={syncAssignments.isPending}
          >
            <RefreshCw
              className={`mr-1.5 h-3.5 w-3.5 ${syncAssignments.isPending ? 'animate-spin' : ''}`}
            />
            {syncAssignments.isPending ? t('admin.syncing') : t('admin.syncAssignments')}
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-sm">
            <Activity className="h-4 w-4" />
            {t('admin.systemHealth')}
            {health && (
              <Badge
                variant="outline"
                className={
                  health.status === 'healthy'
                    ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                    : health.status === 'degraded'
                      ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
                      : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                }
              >
                {health.status}
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {health ? (
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground">Version: {health.version}</p>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-5">
                {Object.entries(health.checks).map(([key, ok]) => (
                  <div key={key} className="flex items-center gap-2 rounded-lg border p-2">
                    <StatusIcon ok={ok} />
                    <span className="text-xs">{formatCheckLabel(key)}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">{t('admin.loadingHealth')}</p>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-5">
        <StatCard label="Profiles" value={stats?.profiles ?? 0} subtitleColor="blue" />
        <StatCard label="Skills" value={stats?.skills ?? 0} subtitleColor="muted" />
        <StatCard label="Assignments" value={stats?.assignments ?? 0} subtitleColor="green" />
        <StatCard label="Matches" value={stats?.matches ?? 0} subtitleColor="blue" />
        <StatCard label="Integrations" value={stats?.integrations ?? 0} subtitleColor="muted" />
      </div>

      <LocationFilterCard />

      <AiConfigCard />

      <UserRolesCard />

      <MemberVisibilityCard />

      <MatrixBuilderCard />

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-sm">
            <Database className="h-4 w-4" />
            {t('admin.activityLog')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {logsLoading ? (
            <p className="text-sm text-muted-foreground">{t('admin.loadingLogs')}</p>
          ) : logs.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('admin.noLogs')}</p>
          ) : (
            <div className="max-h-96 space-y-2 overflow-y-auto">
              {logs.map((log) => (
                <ActivityRow key={log.id} log={log} />
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
